import type React from 'react';
import { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { updateNotificationRequest } from '@/data/notifications/actions';
import Notification from '@/data/notifications/model';
import { type UpdateRequestArgs, updateRequest } from '@/data/request/actions/updateRequest';
import Request, { type RequestFields } from '@/data/request/model';
import type User from '@/data/user/model';
import useNotice from '@/notices/hooks/useNotice';
import ActionButton from '@/notifications/components/ActionButton';
import { NotifyItem } from '@/notifications/components/NotifyItem';
import { formatUserTime } from '@/scheduler/util/formats';
import { getAuthUser } from '@/shared/auth/selectors';
import AvatarImg from '@/shared/ui/AvatarImg';
import { useWiwDispatch, useWiwSelector } from '@/store';

type Props = {
  request: Request;
  notification?: Notification;
  user: User;
  creator?: User;
};
export default function TimeoffRequest({ request, notification, user, creator }: Props) {
  const currentUser = useWiwSelector(getAuthUser);
  const dispatch = useWiwDispatch();

  const history = useHistory();
  const { errorNotice, successNotice } = useNotice();

  const [deleted, setDeleted] = useState<RequestFields>();

  const approveRequest = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const entry = request.set('user_status', Request.Status.ACCEPTED);
    return dispatch(updateRequest(entry.toJSON() as UpdateRequestArgs))
      .unwrap()
      .then(data => {
        successNotice('Request has been approved successfully.');
        setDeleted(data.request);
        return data.request;
      })
      .catch(err => {
        errorNotice('There was an error approving the request. Please make sure you have permission to do this.');
        return err;
      });
  };

  const cancelRequest = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const entry = request.set('user_status', Request.Status.CANCELED);
    return dispatch(updateRequest(entry.toJSON() as UpdateRequestArgs))
      .unwrap()
      .then(data => {
        successNotice('Request has been denied successfully.');
        setDeleted(data.request);
        return data.request;
      })
      .catch(err => {
        errorNotice('There was an error denying the request. Please make sure you have permission to do this.');
        return err;
      });
  };

  const getActionName = (action: string, isOwner: boolean = false) => {
    switch (action) {
      case Notification.Actions.CANCEL:
        return isOwner ? 'canceled' : 'denied';
      case Notification.Actions.APPROVE:
        return 'approved';
      case Notification.Actions.UPDATE_APPROVE:
        return 'updated and approved';
      case Notification.Actions.UPDATE:
        return 'updated';
      default:
        return '';
    }
  };

  const getActions = () => {
    if (user && user.id !== currentUser.id && (!user.isSupervisor() || currentUser.canManage())) {
      return [
        <ActionButton
          requestType={request.type_label ?? ''}
          key="approve"
          title="Approve"
          icon="fa-check-circle"
          type="green"
          click={approveRequest}
        />,
        <ActionButton
          requestType={request.type_label ?? ''}
          key="deny"
          title="Deny"
          icon="fa-times-circle"
          type="red"
          click={cancelRequest}
        />,
      ];
    }
  };
  const getAvatar = () => {
    if (notification && creator) {
      return <AvatarImg user={creator} size={Notification.AVATAR_SIZE} />;
    }
    if (!notification && user) {
      return <AvatarImg user={user} size={Notification.AVATAR_SIZE} />;
    }
  };

  const getDescription = () => {
    const start = request.date('start_time');
    const end = request.date('end_time');
    if (request.allDay()) {
      //For full-day requests we are going to round to noon
      const noonStart = request.noonStart.format('ddd, MMMM D');
      const noonEnd = request.noonEnd.format('ddd, MMMM D');

      if (request.isMultiDay) {
        return (
          <span>
            {' '}
            requested <strong>Time Off</strong> from {noonStart} to {noonEnd}
          </span>
        );
      }
      return (
        <span>
          {' '}
          requested <strong>Time Off</strong> for {noonStart}
        </span>
      );
    }
    return (
      <span>
        {' '}
        requested <strong>Time Off</strong> on {start?.format('ddd, MMMM D')} from {formatUserTime(start, false)} to{' '}
        {formatUserTime(end, false)}
      </span>
    );
  };

  const getMessage = () => {
    const action = notification?.action || '';
    const isOwner =
      action === Notification.Actions.CANCEL
        ? currentUser.id === request.canceled_by
        : currentUser.id === request.user_id;
    switch (action) {
      case Notification.Actions.CREATE:
        if (!isOwner && user) {
          return (
            <span>
              {' '}
              created time off for {user.first_name} {getMessageDate()}
            </span>
          );
        }
        return <span> requested time off {getMessageDate()}</span>;
      case Notification.Actions.CANCEL:
      case Notification.Actions.APPROVE:
      case Notification.Actions.UPDATE:
      case Notification.Actions.UPDATE_APPROVE:
        if (isOwner) {
          return (
            <span>
              {' '}
              {getActionName(action, isOwner)} your time-off request {getMessageDate()}
            </span>
          );
        }
        return (
          <span>
            {' '}
            {getActionName(action, isOwner)} {user ? `time-off for ${user.first_name}` : 'a time-off'}{' '}
            {getMessageDate()}
          </span>
        );
    }
  };

  const getMessageDate = () => {
    const start = request.date('start_time');
    const end = request.date('end_time');
    const noonStart = request.noonStart.format('MMMM D');
    const noonEnd = request.noonEnd.format('MMMM D');

    if (request.allDay()) {
      if (request.isMultiDay) {
        return (
          <Fragment>
            {' '}
            from <strong>{noonStart}</strong> to <strong>{noonEnd}</strong>
          </Fragment>
        );
      }
      return (
        <Fragment>
          {' '}
          for <strong>{noonStart}</strong>
        </Fragment>
      );
    }
    return (
      <Fragment>
        from <strong>{formatUserTime(start, false)}</strong>
        {' to '}
        <strong>{formatUserTime(end, false)}</strong>
        {' on '}
        <strong>{start?.format('MMMM D')}</strong>
      </Fragment>
    );
  };

  const getStatus = () => {
    switch (request.status) {
      case Request.Status.PENDING:
        return { name: 'Pending Approval', style: '' };
      case Request.Status.CANCELED:
        return { name: request.canceled_by === request.user_id ? 'Canceled' : 'Denied', style: 'error' };
      case Request.Status.ACCEPTED:
        return { name: 'Approved', style: 'success' };
      case Request.Status.EXPIRED:
        return { name: 'Expired', style: 'error' };
      default:
        return {};
    }
  };

  const getTitle = () => {
    if (notification && creator) {
      return creator.id === currentUser.id ? 'You' : creator.shortName;
    }
    if (!notification && user) {
      return user.shortName;
    }
  };

  const removeRequest = () => {
    if (deleted) {
      dispatch(updateNotificationRequest(deleted));
    }
  };

  return (
    <NotifyItem
      title={getTitle()}
      description={notification ? getMessage() : getDescription()}
      tag={getStatus()}
      avatar={getAvatar()}
      click={() => history.push(`/requests/time-off/${request.id}`)}
      {...(notification
        ? {}
        : {
            actions: getActions(),
          })}
      deleted={!!deleted}
      remove={removeRequest}
    />
  );
}
